import { ElementAction } from '../../../elements/elementAction';
import { Functions } from '../../../types/types';
import { readFileAsText } from '../../../utils/file';
import { ContentPlanRootState as S } from '../../../elements/contentPlan/store';
import { Metadata } from '../../../types';
import { Toasters } from '../../Toaster/toaster';
import { ManagedData, ManagedDataImport } from '../../../types/ManagedData';
import { ThunkDispatch } from 'redux-thunk';
import { useDispatch } from 'react-redux';
import { ApiResponseMessage } from '../../../utils/constants';

export const validateManagedDataFile =
  (file: File): ElementAction<Functions, S> =>
  async (dispatch, getState, { getAPI }) => {
    if (!file.name.toLowerCase().endsWith('.rpjs')) {
      throw new Error(
        'ERROR: Please upload an update file with a .rpjs extension.'
      );
    }
    const fileContent = await readFileAsText(file);
    if (!fileContent?.trim()) throw new Error('File is Empty');
    const jsonData = JSON.parse(fileContent);
    return {
      managedData: jsonData,
      managedDataImport: { fileName: file.name },
      report: {
        '# of mission-critical priorities in the file':
          jsonData?.rolePriorities?.length,
        '# of journey steps in the file': jsonData?.rolePriorities?.reduce(
          (acc, curr) => acc + (curr.priorityJourneySteps?.length || 0),
          0
        ),
        '# of positions in the file': jsonData?.positions?.length || 0,
        '# of categories in the file': jsonData?.topicCategories?.length || 0,
        '# of perennial priorities in the file':
          jsonData?.perennialPriorities?.length || 0
      }
    };
  };

export const importManagedData =
  (
    managedDataImport: ManagedDataImport,
    managedData: ManagedData,
    toasters: Toasters<Partial<Metadata>>,
    closeModal: Function
  ): ElementAction<Functions, S> =>
  async (dispatch, getState, { getAPI }) => {
    const { importManagedData: importManagedDataAPI } = getAPI();
    const toaster = toasters.createToaster();
    try {
      await importManagedDataAPI({
        managedData,
        managedDataImport
      });
      toaster.success(
        'Upload in progress. You will receive an email with additional details once completed.'
      );
    } catch (err) {
      const errorMessage = err?.message || err?.toString() || '';
      if (errorMessage.includes(ApiResponseMessage.EndpointRequestTimedOut)) {
        toaster.warn(
          'Upload is taking longer than expected due to large amount of data. If you do not receive email in 5 minutes, please try again.'
        );
      } else {
        toaster.error(
          'Error occurred while importing managed data. Please try again later.'
        );
      }
    } finally {
      closeModal();
    }
  };

export const useActions = () => {
  const dispatch: ThunkDispatch<S, any, any> = useDispatch();
  return {
    validateManagedDataFile: async (file: File) =>
      await dispatch(validateManagedDataFile(file)),
    importManagedData: async (
      managedDataImport: ManagedDataImport,
      managedData: ManagedData,
      toasters: Toasters<Partial<Metadata>>,
      closeModal: Function
    ) =>
      await dispatch(
        importManagedData(managedDataImport, managedData, toasters, closeModal)
      )
  };
};
